<div class="localized-descriptions-editor">
  <div class="d-flex align-items-center justify-content-between m-b-10">
    <div class="d-flex align-items-center">
      <h4 class="m-b-0">Localized descriptions</h4>
      @if (missingTranslations) {
        <span class="c-red m-l-10 pill red sm">
          <fa-icon icon="exclamation-triangle"></fa-icon>
          Missing translations
        </span>
      } @else {
        <span class="c-red m-l-10 pill green sm">
          <fa-icon icon="check"></fa-icon>
          Fully Translated
        </span>
      }
    </div>
    <div class="d-flex align-items-center">
      <button type="button" class="btn btn-sm btn-blank m-r-10" [disabled]="translating" (click)="showConfig = !showConfig">Configure Auto-translate</button>
      <button type="button" class="btn btn-sm btn-secondary m-r-10" [disabled]="translating || saving" (click)="autoTranslate()">
        {{ translating ? "Translating…" : "Auto-translate" }}
      </button>
      <button type="button" class="btn btn-sm btn-primary" [disabled]="saving || translating" (click)="save()">
        {{ saving ? "Saving…" : saved ? "Saved!" : "Save descriptions" }}
      </button>
    </div>
  </div>

  @if (showConfig) {
    <div class="localized-config-panel p-15 m-b-15" style="background: rgba(0, 0, 0, 0.03); border-radius: 6px">
      <label class="t-14 b m-b-5">Terms to never translate</label>
      <p class="t-12 c-gray m-b-5">Comma-separated. These are sent to the translator and kept verbatim in every language (brand and product names, etc.).</p>
      <textarea class="form-control t-14" rows="3" [(ngModel)]="doNotTranslateTerms" name="doNotTranslateTerms"></textarea>
      <div class="d-flex m-t-10">
        <button type="button" class="btn btn-sm btn-primary m-r-10" [disabled]="savingConfig" (click)="saveConfig()">
          {{ savingConfig ? "Saving…" : "Save config" }}
        </button>
        <button type="button" class="btn btn-sm btn-blank" [disabled]="savingConfig" (click)="showConfig = false">Close</button>
      </div>
    </div>
  }

  <p class="t-12 c-gray m-b-15">
    Author the English copy, then Auto-translate to fill the other languages. Missing translations fall back to English on the product page. Descriptions may contain HTML.
  </p>

  <!-- Per-language translation progress. -->
  @if (missingTranslations) {
    <div class="d-flex flex-wrap m-b-15">
      @for (lang of otherLanguages; track lang.code) {
        <span
          class="pill sm m-r-5 m-b-5"
          [class.gray]="langStatus[lang.code] === 'idle' || langStatus[lang.code] === 'translating'"
          [class.green]="langStatus[lang.code] === 'done'"
          [class.red]="langStatus[lang.code] === 'error'"
        >
          @if (langStatus[lang.code] === "translating") {
            <fa-icon icon="spinner" animation="spin"></fa-icon>
          } @else if (langStatus[lang.code] === "done") {
            <fa-icon icon="check"></fa-icon>
          } @else if (langStatus[lang.code] === "error") {
            <fa-icon icon="exclamation-triangle"></fa-icon>
          }
          {{ lang.code }}
        </span>
      }
    </div>
  }

  <div class="d-flex m-b-10">
    <span class="radio-option" (click)="descriptionContentType = 'visual'" [class.selected]="descriptionContentType === 'visual'">
      @if (descriptionContentType === "visual") {
        <fa-icon icon="check-circle"></fa-icon>
      }
      Visual Editor
    </span>
    <span class="radio-option" (click)="descriptionContentType = 'html'" [class.selected]="descriptionContentType === 'html'">
      @if (descriptionContentType === "html") {
        <fa-icon icon="check-circle"></fa-icon>
      }
      HTML
    </span>
  </div>

  @if (error) {
    <div class="alert alert-danger t-12">{{ error }}</div>
  }

  <!-- English source (unified field) — always visible. -->
  <div class="localized-lang-block">
    <div class="d-flex align-items-center m-b-5">
      <strong class="t-14">English</strong>
      <span class="t-12 c-gray m-l-5">(en) — source</span>
    </div>
    <div class="form-group m-b-5">
      <label class="t-12 c-gray">Description</label>
      @if (descriptionContentType === "visual" && quillReady) {
        <fui-quill-editor
          [content]="descriptions.en"
          (contentChange)="descriptions.en = $event; markDirty()"
          placeholder="Enter a product description..."
          height="150px"
          [toolbarOptions]="[['bold', 'italic', 'underline', 'strike'], [{ header: [1, 2, 3, false] }], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']]"
        ></fui-quill-editor>
      }
      @if (descriptionContentType === "html") {
        <textarea class="form-control t-14" rows="3" [(ngModel)]="descriptions.en" name="desc_en" (ngModelChange)="markDirty()"></textarea>
      }
    </div>
    <div class="form-group m-b-10">
      <label class="t-12 c-gray">Short description</label>
      <textarea class="form-control t-14" rows="2" [(ngModel)]="shortDescriptions.en" name="short_en" (ngModelChange)="markDirty()"></textarea>
    </div>
  </div>

  <button type="button" class="btn btn-sm btn-secondary p-l-0 m-b-10" (click)="showAllLanguages = !showAllLanguages">
    {{ showAllLanguages ? "Hide other languages" : "Show all languages (" + otherLanguages.length + ")" }}
  </button>

  @if (showAllLanguages) {
    @for (lang of otherLanguages; track lang.code) {
      <div class="localized-lang-block">
        <div class="d-flex align-items-center m-b-5">
          @if (lang.flag_icon) {
            <img [src]="lang.flag_icon" class="m-r-5" style="width: 20px; height: auto" />
          }
          <strong class="t-14">{{ lang.translated_name || lang.name }}</strong>
          <span class="t-12 c-gray m-l-5">({{ lang.code }})</span>
          @if (!descriptions[lang.code] && !shortDescriptions[lang.code]) {
            <span class="t-12 c-gray m-l-5">— falls back to English</span>
          }
        </div>
        <div class="form-group m-b-5">
          <label class="t-12 c-gray">Description</label>
          @if (descriptionContentType === "visual" && quillReady) {
            <fui-quill-editor
              [content]="descriptions[lang.code]"
              (contentChange)="descriptions[lang.code] = $event; markDirty()"
              placeholder="Enter a product description..."
              height="150px"
              [toolbarOptions]="[['bold', 'italic', 'underline', 'strike'], [{ header: [1, 2, 3, false] }], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']]"
            ></fui-quill-editor>
          }
          @if (descriptionContentType === "html") {
            <textarea class="form-control t-14" rows="3" [(ngModel)]="descriptions[lang.code]" [name]="'desc_' + lang.code" (ngModelChange)="markDirty()"></textarea>
          }
        </div>
        <div class="form-group m-b-20">
          <label class="t-12 c-gray">Short description</label>
          <textarea class="form-control t-14" rows="2" [(ngModel)]="shortDescriptions[lang.code]" [name]="'short_' + lang.code" (ngModelChange)="markDirty()"></textarea>
        </div>
      </div>
    }
  }
</div>
